import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import { ListItemTitle } from "./documents/ListItemTitle";
import { useTreeItemControlTabIndex } from "./useSidebarNav";

/** Icon button in the header's right slot; 40px touch target on phones */
export const SECTION_HEADER_ACTION_CLASS_NAME = twMerge(
  "right-button",
  "flex items-center justify-center rounded-6 sm:w-10 sm:h-10",
  "hoverable-icon-main hoverable-icon-secondary hover:text-icon-active",
);

/** Phone-size icon for {@link SECTION_HEADER_ACTION_CLASS_NAME} buttons */
export const SECTION_HEADER_ACTION_ICON_CLASS_NAME = "sm:w-[28px] sm:h-[28px]";

export function SidebarSectionHeader({
  isOpen,
  onToggle,
  title,
  icon,
  compact,
  children,
}: {
  isOpen: boolean;
  onToggle: () => void;
  title: string;
  icon?: React.ReactNode;
  /** 22px uppercase mono label row (Figma "Panel Divider") instead of the folder-style header */
  compact?: boolean;
  children?: React.ReactNode;
}) {
  const controlTabIndex = useTreeItemControlTabIndex();
  return (
    <div
      className={twMerge(
        "relative flex group rounded-6 select-none",
        "hover:bg-sidebar-button-hover-inverted-bg sm:hover:bg-transparent",
        "[&:has(.right-button:hover)]:bg-transparent",
      )}
    >
      <button
        type="button"
        tabIndex={controlTabIndex}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={title}
        className={twMerge(
          "flex items-center w-full cursor-pointer",
          compact
            ? "h-[22px] sm:h-10 pl-1 gap-1 sm:gap-1.5 pr-7 sm:pr-12"
            : "min-h-7.5 pr-7 sm:pr-10",
        )}
      >
        {compact ? (
          <div className="flex w-[10px] h-[10px] sm:w-4 sm:h-4">
            <Icon
              name="ArrowDown"
              className={twMerge(
                "m-auto sm:w-3 sm:h-[7px] [&>path]:stroke-document-text",
                !isOpen && "-rotate-90",
              )}
            />
          </div>
        ) : (
          <div className="min-w-4 mx-[3px]">
            <Icon
              name="ArrowDownLarge"
              width={16}
              height={16}
              className={twMerge("m-auto", !isOpen && "-rotate-90")}
            />
          </div>
        )}
        {icon}
        {compact ? (
          <div className="font-mono text-[12px] leading-4 sm:text-[15px] sm:leading-5 tracking-[-0.02em] uppercase text-document-text">
            {title}
          </div>
        ) : (
          <ListItemTitle type="folder" title={title} />
        )}
      </button>
      {children}
    </div>
  );
}
